/* Maps cart and product-list API responses to the shapes the cart page uses. */
import { getRewardBaseDenomination } from "../Components/Utility/Utility";

/* CartDto items are products, each with one or more denominations
   (productDetails). The page shows one line per product + denomination. */
export const toCartLines = (cart) =>
  (cart?.items || []).flatMap((product) =>
    (product?.productDetails || [])
      .filter((detail) => Number(detail?.denomination) > 0 && Number(detail?.qty) > 0)
      .map((detail) => ({
        lineId: `${product.cartItemId}-${detail.denomination}`,
        cartItemId: product.cartItemId,
        productId: product.productId,
        sku: product?.sku ? String(product.sku).trim() : null,
        name: product?.name,
        supplierCode: product?.supplierCode,
        productImage: product?.productImage,
        denomination: Number(detail.denomination),
        quantity: Number(detail.qty),
      }))
  );

/* ProductListDataDto from GET /productlist?sku= reduced to what the cart needs.
   Stock is per denomination (clientProductDenomination), as on checkout. */
export const toProductDetails = (product) => ({
  sku: product?.sku,
  name: product?.name,
  image: product?.mobile || product?.thumbnail || product?.small || "",
  isAvailable: Boolean(product?.enabled) && !product?.deprecated,
  customerDiscount: Number(product?.customerDiscount) || 0,
  convenienceFeePercent: Number(product?.convenienceFee) || 0,
  gstPercent: Number(product?.gst) || 0,
  limits: product?.productBudget?.limits || [],
  // Earn: minEarnPoints is the earn value and maxEarnPoints the burn value, as on the product page.
  minEarnPoints: Number(product?.minEarnPoints) || 0,
  maxEarnPoints: Number(product?.maxEarnPoints) || 0,
  // Rates are set against the product's lowest denomination, disabled ones included.
  rewardBaseDenomination: getRewardBaseDenomination({
    product,
    eligibleDenominations: product?.clientProductDenomination || [],
    fallbackDenominations: product?.clientProductDenomination || [],
  }),
  denominations: (product?.clientProductDenomination || []).reduce((map, item) => {
    map[Number(item?.denomination)] = {
      stockOut: item?.stockOut === true,
      isAvailable: item?.enabled !== false && item?.deleted !== true,
    };
    return map;
  }, {}),
});

/* Add-to-cart payload that sets every given cart line to `qty` (the new
   quantity, not a difference; 0 removes the line). Lines of the same product
   go in one item with one productDetails entry per denomination. */
export const toSetLinesPayload = (memberId, lines, qty) => ({
  memberId,
  items: Object.values(
    (lines || []).reduce((items, line) => {
      const key = line.cartItemId || `${line.productId}-${line.supplierCode ?? ""}`;
      items[key] = items[key] || {
        productId: line.productId,
        sku: line.sku,
        name: line.name,
        supplierCode: line.supplierCode,
        productImage: line.productImage,
        productDetails: [],
      };
      items[key].productDetails.push({ denomination: line.denomination, qty });
      return items;
    }, {})
  ),
});

/* Payload that sets one cart line to `qty`. */
export const toAddToCartPayload = (memberId, line, qty) => toSetLinesPayload(memberId, [line], qty);
